import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, useAction, useApi } from '../api';
import { useCan } from '../auth';
import { Ago, Empty, fmtDate, MutationError, Outcome, PageHead, Q, Section, Tag } from '../ui/kit';
import { EventDrawer, EventTable, type EventRow } from './Activity';

const toLocal = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

export function Investigations() {
  const can = useCan();
  const nav = useNavigate();
  const q = useApi<any[]>('/investigations');
  const users = useApi<any[]>('/users/options');
  const devices = useApi<{ items: any[] }>('/devices?limit=200');
  const apps = useApi<any[]>('/apps/options');
  const [form, setForm] = useState({ title: '', userId: '', deviceId: '', appId: '', from: toLocal(new Date(Date.now() - 7 * 86400e3)), to: toLocal(new Date()) });
  const create = useAction(
    () => api('/investigations', { method: 'POST', body: { title: form.title, scope: { userId: form.userId || null, deviceId: form.deviceId || null, appId: form.appId || null, from: new Date(form.from).toISOString(), to: new Date(form.to).toISOString() } } }),
    (r: any) => nav(`/investigate/investigations/${r.id}`),
  );
  return (
    <>
      <PageHead title="Investigations" sub="Group activity around a user, device, application and time window to answer: what happened, who did it, where was data going, and what policy acted." />
      {can('investigations:write') && (
        <Section title="Start an investigation">
          <form className="form-grid mt-12" onSubmit={(e) => { e.preventDefault(); create.mutate(undefined); }}>
            <label className="field">Title<input required maxLength={200} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Customer list shared with personal AI" /></label>
            <label className="field">User<select value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })}><option value="">Any</option>{users.data?.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
            <label className="field">Device<select value={form.deviceId} onChange={(e) => setForm({ ...form, deviceId: e.target.value })}><option value="">Any</option>{devices.data?.items.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}</select></label>
            <label className="field">Application<select value={form.appId} onChange={(e) => setForm({ ...form, appId: e.target.value })}><option value="">Any</option>{apps.data?.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
            <label className="field">From<input type="datetime-local" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} /></label>
            <label className="field">To<input type="datetime-local" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} /></label>
            <div className="self-end"><button className="btn primary" disabled={create.isPending}>Create</button></div>
          </form>
          <MutationError error={create.error} />
        </Section>
      )}
      <Section title="Investigations">
        <Q q={q}>
          {(rows) =>
            rows.length === 0 ? <Empty title="No investigations yet">Open one from an <Link to="/investigate/alerts">alert</Link>, or start one above.</Empty> : (
              <table className="t">
                <thead><tr><th>Title</th><th>Status</th><th>Window</th><th>Created by</th><th>Updated</th></tr></thead>
                <tbody>{rows.map((i) => (
                  <tr key={i.id}><td><Link to={`/investigate/investigations/${i.id}`}>{i.title}</Link></td><td><Tag tone={i.status === 'open' ? 'warn' : 'log'}>{i.status === 'open' ? 'Open' : 'Closed'}</Tag></td>
                    <td className="sub">{fmtDate(i.scope.from)} – {fmtDate(i.scope.to)}</td><td className="sub">{i.created_by_email}</td><td className="sub"><Ago at={i.updated_at} /></td></tr>
                ))}</tbody>
              </table>
            )
          }
        </Q>
      </Section>
    </>
  );
}

function Tally({ title, rows, render }: { title: string; rows: Array<{ name: string; count: number }>; render?: (n: string) => React.ReactNode }) {
  return (
    <div>
      <h3 className="mt-16">{title}</h3>
      {rows.length === 0 ? <p className="muted">—</p> : (
        <table className="t"><tbody>{rows.map((r) => <tr key={r.name}><td>{render ? render(r.name) : r.name}</td><td className="num">{r.count}</td></tr>)}</tbody></table>
      )}
    </div>
  );
}

export function InvestigationDetail() {
  const { id } = useParams();
  const can = useCan();
  const q = useApi<any>(`/investigations/${id}`);
  const [open, setOpen] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [summary, setSummary] = useState<string | null>(null);
  const patch = useAction((b: object) => api(`/investigations/${id}`, { method: 'PATCH', body: b }));
  const addNote = useAction((b: object) => api(`/investigations/${id}/notes`, { method: 'POST', body: b }), () => setNote(''));
  return (
    <Q q={q}>
      {({ investigation: inv, notes, alerts, scopeNames, timeline }) => (
        <>
          <PageHead
            crumbs={<Link to="/investigate/investigations">Investigations</Link>}
            title={inv.title}
            sub={<>
              {scopeNames.user && <>User <Link to={`/discover/users/${scopeNames.user.id}`}>{scopeNames.user.name}</Link> · </>}
              {scopeNames.device && <>Device <Link to={`/discover/devices/${scopeNames.device.id}`}>{scopeNames.device.name}</Link> · </>}
              {scopeNames.app && <>Application <Link to={`/discover/apps/${scopeNames.app.id}`}>{scopeNames.app.name}</Link> · </>}
              {fmtDate(inv.scope.from)} – {fmtDate(inv.scope.to)}
            </>}
            actions={can('investigations:write') && <button className="btn" onClick={() => patch.mutate({ status: inv.status === 'open' ? 'closed' : 'open' })}>{inv.status === 'open' ? 'Close investigation' : 'Reopen'}</button>}
          />
          <MutationError error={patch.error} />
          <div className="cols cols-2-1">
            <div>
              <Section title="Timeline" aside={`${timeline.total} events${timeline.total > 200 ? ' (showing latest 200)' : ''}`}>
                <EventTable rows={timeline.events as EventRow[]} onOpen={setOpen} />
              </Section>
            </div>
            <div>
              <Section title="Summary">
                <Tally title="Outcomes" rows={timeline.summary.byOutcome} render={(n) => <Outcome o={n} />} />
                <Tally title="Destinations" rows={timeline.summary.byApp} />
                <Tally title="Data involved" rows={timeline.summary.byClassification} />
                <Tally title="Policies that acted" rows={timeline.summary.byPolicy} />
                <Tally title="Account types" rows={timeline.summary.byAccountType} />
              </Section>
              {alerts.length > 0 && (
                <Section title="Linked alerts">{alerts.map((a: any) => <div key={a.id} className="note"><Link to={`/investigate/alerts/${a.id}`}>{a.title}</Link></div>)}</Section>
              )}
              <Section title="Findings">
                {can('investigations:write') ? (
                  <div className="stack-8 mt-12">
                    <textarea aria-label="Findings" value={summary ?? inv.summary} onChange={(e) => setSummary(e.target.value)} maxLength={4000} placeholder="What happened, and what was decided?" />
                    <div><button className="btn" disabled={summary === null} onClick={() => patch.mutate({ summary })}>Save findings</button></div>
                  </div>
                ) : <p>{inv.summary || <span className="muted">No findings recorded.</span>}</p>}
              </Section>
              <Section title="Notes">
                {notes.map((n: any) => <div className="note" key={n.id}><header>{n.author_email} · <Ago at={n.created_at} /></header>{n.body}</div>)}
                {can('investigations:write') && (
                  <form className="stack-8 mt-12" onSubmit={(e) => { e.preventDefault(); if (note.trim()) addNote.mutate({ body: note }); }}>
                    <textarea aria-label="Add note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" />
                    <div><button className="btn" disabled={!note.trim()}>Add note</button></div>
                  </form>
                )}
              </Section>
            </div>
          </div>
          {open && <EventDrawer id={open} onClose={() => setOpen(null)} />}
        </>
      )}
    </Q>
  );
}
